import DIBRenderSpace from "./dib-render-space";

/**
 * @category Render Spaces
 * @description have a primary class for handling canvas based render spaces, which is most of them.  This class will handle the canvas element and the bounds management
 
*/
export class CanvasBasedRenderSpace extends DIBRenderSpace {
  // manageBounds determines if we want the renderspace class managing the size of the canvas
  protected manageBounds: boolean = true;
  protected canvas: HTMLCanvasElement;
  protected _bounbsBaseObject: HTMLElement = null;

  constructor(canvas?: HTMLCanvasElement) {
    super();

    this.stampVersion();

    // grab a canvas element from a query selector
    if (canvas instanceof HTMLCanvasElement) {
      this.setCanvas(canvas);
    }
  }

  public setCanvas(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    // set the bounds from the current canvas
    const windowWidth: number = window.innerWidth;
    const windowHeight: number = window.innerHeight;

    // get the canvas bounds from the actual pixel positions
    const currentSize: DOMRect = canvas.getBoundingClientRect();

    // default to the current canvas' size and position
    this.setBounds(
      currentSize.left / windowWidth,
      currentSize.top / windowHeight,
      currentSize.width / windowWidth,
      currentSize.height / windowHeight
    );
  }

  public setZIndex(zIndex: number) {
    this.getCanvas().style.zIndex = `${zIndex}`;
  }

  // ================================================
  //          BOUNDS MANAGEMENT
  // ================================================

  public setBounds(x: number, y: number, width: number, height: number): void {
    super.setBounds(x, y, width, height);

    this.updateCanvasSize();
  }

  public updateCanvasSize(): void {
    // dont do this if the canvas isnt set yet
    if (!this.canvas) return;
    if (!this.manageBounds) return;

    // get the window size
    let bounds: { x: number; y: number; width: number; height: number } =
      this.getNormalizedBounds();

    let px = (val: number): string => `${val}px`;

    this.canvas.style.position = "absolute";
    this.canvas.style.left = px(bounds.x);
    this.canvas.style.top = px(bounds.y);
    this.canvas.style.width = px(bounds.width);
    this.canvas.style.height = px(bounds.height);
  }

  // take the normalized bounds and map them to a rectangle
  public getNormalizedBounds(): {
    x: number;
    y: number;
    width: number;
    height: number;
  } {
    const boundsBase = this.getBoundingParentSize();

    const windowWidth: number = window.innerWidth;
    const windowHeight: number = window.innerHeight;

    const scaleW = (val: number): number => val * boundsBase.width;
    const scaleH = (val: number): number => val * boundsBase.height;

    return {
      x: scaleW(this.normX),
      y: scaleH(this.normY),
      width: scaleW(this.normWidth),
      height: scaleH(this.normHeight),
    };
  }

  public getBoundingParentSize(): { width: number; height: number } {
    if (!this._bounbsBaseObject) {
      return { width: window.innerWidth, height: window.innerHeight };
    }

    return {
      width: this._bounbsBaseObject.clientWidth,
      height: this._bounbsBaseObject.clientHeight,
    };
  }

  setBoundsBaseObject(boundsBase: HTMLElement) {
    this._bounbsBaseObject = boundsBase;
  }

  public getWidth(): number {
    return this.canvas.getBoundingClientRect().width;
  }

  public getHeight(): number {
    return this.canvas.getBoundingClientRect().height;
  }

  public getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  public onWindowResize(): void {
    super.onWindowResize();
    this.updateCanvasSize();
  }

  public getEventTarget() {
    return this.getCanvas();
  }
}

CanvasBasedRenderSpace["Version"] = "1.0.0";

export default CanvasBasedRenderSpace;
